import { Card } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, CardContent, Stack } from "@mui/material";
import { BaseLink } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { WorkplaceNameWithDistance } from "@src/appV2/redesign/Shift/Open/WorkplaceNameWithDistance";
import {
  type InterviewScheduleStatus,
  type InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import { useBookingOrderingCohort } from "../../hooks/useBookingOrderingCohort";
import { type PlacementData } from "../../types/placement";
import { PlacementWorkerTypePill } from "../PlacementCard/components/PlacementWorkerTypePill";
import { BookedInterviewCardTitle } from "./components/BookedInterviewCardTitle";
import { InterviewScheduleStatusPill } from "./components/InterviewScheduleStatusPill";
import { InterviewStatusPill } from "./components/InterviewStatusPill";

interface InterviewPlacementCardProps {
  interview: {
    start: Date;
    end: Date;
    status: InterviewStatus;
    scheduleStatus?: InterviewScheduleStatus | undefined;
  };
  placement: Pick<PlacementData, "id" | "workerTypes">;
  distance: number;
  workplaceName: string;
  pageIndex: number;
  cardIndex: number;
  placementCandidateId: string;
  workerTimeZone: string;
  placementDetailsPath: string;
  source: "booked_interviews" | "sent_applications" | "easy_scheduling_placements";
}

export function InterviewPlacementCard(props: InterviewPlacementCardProps) {
  const {
    interview,
    placement,
    distance,
    workplaceName,
    placementCandidateId,
    pageIndex,
    cardIndex,
    workerTimeZone,
    placementDetailsPath,
    source,
  } = props;
  const { start, end, status, scheduleStatus } = interview;
  const { workerTypes } = placement;
  const bookingReorderingCohort = useBookingOrderingCohort();

  return (
    <Card
      variant="primary"
      sx={{
        border: (theme) => `1px solid ${theme.palette.border?.subtle}`,
        background: (theme) => theme.palette.background.quaternary,
      }}
    >
      <CardActionArea
        component={BaseLink}
        to={placementDetailsPath}
        onClick={() => {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_VIEWED, {
            placementCandidateId,
            placementDetails: placement,
            page: pageIndex + 1,
            listIndex: cardIndex + 1,
            source,
            bookingReorderingCohort,
          });
        }}
      >
        <CardContent>
          <Stack spacing={5} sx={{ paddingTop: 2 }}>
            <Stack spacing={3}>
              <BookedInterviewCardTitle
                interviewStart={start}
                interviewEnd={end}
                workerTimezone={workerTimeZone}
              />
              <WorkplaceNameWithDistance
                textVariant="body1"
                workplaceName={workplaceName}
                formattedDistance={formatDistanceInMiles(distance)}
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                {isDefined(workerTypes) && <PlacementWorkerTypePill workerTypes={workerTypes} />}
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                {isDefined(scheduleStatus) && (
                  <InterviewScheduleStatusPill
                    scheduleStatus={scheduleStatus}
                    interviewStart={start}
                    interviewEnd={end}
                  />
                )}
                <InterviewStatusPill status={status} />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
